/**
 * Group Service - API methods for group management
 */

import apiClient from './apiClient';
import { Permission } from './permissionService';
import { Role } from './roleService';

export type GroupType = 'LOCATION' | 'WORKFLOW' | 'CUSTOM';

// Group member with role info
export interface GroupMemberWithRole {
  id: string; // organizationMemberId
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  userStatus: string;
  roles?: { id: string; name: string }[];
}

// Group type
export interface Group {
  id: string;
  name: string;
  type: GroupType;
  description?: string;
  organizationId: string;
  memberCount?: number;
  clientCount?: number;
  roles?: Role[];
  permissions?: Permission[];
  createdAt: string;
  updatedAt: string;
}


// Client item assigned to a group
export interface GroupClientItem {
  organizationMemberId: string;
  firstName: string;
  lastName: string;
  email: string;
  location?: string;
  assignedAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Create group request
export interface CreateGroupRequest {
  name: string;
  type?: GroupType;
  description?: string;
  memberIds?: string[];
  roleIds?: string[];
  permissionIds?: string[];
}

// Update group request
export interface UpdateGroupRequest {
  name?: string;
  description?: string;
}

// List groups params
export interface ListGroupsParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
}

// List members params
export interface ListMembersParams {
  page?: number;
  limit?: number;
  search?: string;
}

/**
 * Get paginated list of groups in organization
 */
export const getGroups = async (
  params: ListGroupsParams = {},
): Promise<PaginatedResponse<Group>> => {
  const response = await apiClient.get<{ success: boolean } & PaginatedResponse<Group>>(
    '/api/v1/groups',
    { params },
  );
  return {
    data: response.data.data,
    meta: response.data.meta,
  };
};

/**
 * Get group by ID with roles and permissions
 */
export const getGroupById = async (id: string): Promise<Group> => {
  const response = await apiClient.get<{ success: boolean; data: Group }>(`/api/v1/groups/${id}`);
  return response.data.data;
};

/**
 * Create new group
 */
export const createGroup = async (data: CreateGroupRequest): Promise<Group> => {
  const response = await apiClient.post<{ success: boolean; data: Group }>('/api/v1/groups', data);
  return response.data.data;
};

/**
 * Update group details (name, description)
 */
export const updateGroup = async (id: string, data: UpdateGroupRequest): Promise<Group> => {
  const response = await apiClient.patch<{ success: boolean; data: Group }>(
    `/api/v1/groups/${id}`,
    data,
  );
  return response.data.data;
};

/**
 * Delete group
 */
export const deleteGroup = async (id: string): Promise<{ message: string }> => {
  const response = await apiClient.delete<{ success: boolean; message: string }>(
    `/api/v1/groups/${id}`,
  );
  return { message: response.data.message };
};

// ==================== Member Management ====================

/**
 * Get group members (with role info)
 */
export const getGroupMembers = async (
  id: string,
  params: ListMembersParams = {},
): Promise<PaginatedResponse<GroupMemberWithRole>> => {
  const response = await apiClient.get<{ success: boolean } & PaginatedResponse<GroupMemberWithRole>>(
    `/api/v1/groups/${id}/members`,
    { params },
  );
  return {
    data: response.data.data,
    meta: response.data.meta,
  };
};

/**
 * Add members to group
 */
export const addGroupMembers = async (id: string, memberIds: string[]): Promise<Group> => {
  const response = await apiClient.post<{ success: boolean; data: Group }>(
    `/api/v1/groups/${id}/members`,
    { memberIds },
  );
  return response.data.data;
};

/**
 * Remove members from group
 */
export const removeGroupMembers = async (id: string, memberIds: string[]): Promise<Group> => {
  const response = await apiClient.delete<{ success: boolean; data: Group }>(
    `/api/v1/groups/${id}/members`,
    { data: { memberIds } },
  );
  return response.data.data;
};

// ==================== Role & Permission Assignment ====================

/**
 * Assign roles to group (replaces existing)
 */
export const assignRolesToGroup = async (id: string, roleIds: string[]): Promise<Group> => {
  const response = await apiClient.put<{ success: boolean; data: Group }>(
    `/api/v1/groups/${id}/roles`,
    { roleIds },
  );
  return response.data.data;
};

/**
 * Assign direct permissions to group (replaces existing)
 */
export const assignPermissionsToGroup = async (
  id: string,
  permissionIds: string[],
): Promise<Group> => {
  const response = await apiClient.put<{ success: boolean; data: Group }>(
    `/api/v1/groups/${id}/permissions`,
    { permissionIds },
  );
  return response.data.data;
};

// ==================== Client Access ====================

/**
 * Get clients assigned to a group
 */
export const getGroupClients = async (
  groupId: string,
  params: { page?: number; limit?: number } = {},
): Promise<PaginatedResponse<GroupClientItem>> => {
  const response = await apiClient.get<{ success: boolean } & PaginatedResponse<GroupClientItem>>(
    `/api/v1/groups/${groupId}/clients`,
    { params },
  );
  return { data: response.data.data, meta: response.data.meta };
};

/**
 * Add clients to a group
 */
export const addGroupClients = async (groupId: string, clientIds: string[]): Promise<void> => {
  await apiClient.post(`/api/v1/groups/${groupId}/clients`, { clientIds });
};

/**
 * Remove a client from a group
 */
export const removeGroupClient = async (groupId: string, clientId: string): Promise<void> => {
  await apiClient.delete(`/api/v1/groups/${groupId}/clients/${clientId}`);
};

const groupService = {
  getGroups,
  getGroupById,
  createGroup,
  updateGroup,
  deleteGroup,
  getGroupMembers,
  addGroupMembers,
  removeGroupMembers,
  assignRolesToGroup,
  assignPermissionsToGroup,
  getGroupClients,
  addGroupClients,
  removeGroupClient,
};

export default groupService;
